import React, { useEffect, useState } from "react";
import { Alert, Button, Select, Space } from "antd";
import { useTranslation } from "react-i18next";
import { listTransportRoutesForSelection } from "../transports/api/transportRoutesApi";
import type { TransportRouteSummaryDto } from "../../dto/transport";
import { getTransportLabel } from "../../utils/transportLabel";

export default function ManualPZTransportPicker({
  value,
  onChange,
  title,
  hint,
}: {
  value: string[];
  onChange: (ids: string[]) => void;
  /** Overrides the manual-receipt wording (e.g. "Inny transport" on a ZD's PZ). */
  title?: string;
  hint?: string;
}) {
  const { t } = useTranslation("pz");
  const [routes, setRoutes] = useState<TransportRouteSummaryDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setFailed(false);
    listTransportRoutesForSelection()
      .then((rows) => {
        if (active) setRoutes(rows);
      })
      .catch(() => {
        if (active) setFailed(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [revision]);
  return (
    <Space direction="vertical" style={{ width: "100%" }}>
      <strong>{title || t("manualReceipt.transportTitle")}</strong>
      <span>{hint || t("manualReceipt.transportHint")}</span>
      {failed && (
        <Alert
          type="error"
          message={t("manualReceipt.transportFailed")}
          action={
            <Button onClick={() => setRevision((n) => n + 1)}>
              {t("manualReceipt.retry")}
            </Button>
          }
        />
      )}
      <Select
        mode="multiple"
        allowClear
        showSearch
        optionFilterProp="label"
        aria-label={title || t("manualReceipt.transportTitle")}
        placeholder={t("manualReceipt.noTransport")}
        style={{ width: "100%" }}
        loading={loading}
        disabled={failed || loading}
        value={value}
        onChange={onChange}
        options={routes
          .filter((row) => row.status !== "CANCELLED" || value.includes(row.id))
          .map((row) => ({
            value: row.id,
            label: [
              getTransportLabel(row),
              row.transportCompany,
              row.originCity,
              row.destinationCity,
            ]
              .filter(Boolean)
              .join(" · "),
          }))}
      />
    </Space>
  );
}
